/**
 * @fileoverview Teacher notes textarea with silent debounced autosave (AR-753).
 *
 * Shared by the lesson (TeacherAssessmentDrawer) and final
 * (TeacherFinalAssessmentDrawer) drawers, rendered only when exactly one
 * student is selected. Saves go to the assessment's master journal with no
 * toast, mirroring the student drawer's `autosaveJournal` pattern in
 * `ArcStudentAssessmentDrawerContent`. Parents remount it per
 * student/journal via `key` so local state resets on selection change.
 */

import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { Textarea } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { NOTES_AUTOSAVE_DEBOUNCE_MS, NOTES_TEXTAREA_MIN_ROWS } from '../constants'

interface AssessmentNotesSectionProps {
  initialValue: string
  disabled?: boolean
  onPersist: (description: string) => Promise<unknown>
}

export function AssessmentNotesSection({
  initialValue,
  disabled,
  onPersist,
}: AssessmentNotesSectionProps) {
  const { t } = useTranslation()
  const [value, setValue] = useState(initialValue)
  const saveTimeout = useRef<ReturnType<typeof setTimeout>>()
  const valueRef = useRef(value)
  const onPersistRef = useRef(onPersist)
  valueRef.current = value
  onPersistRef.current = onPersist

  // Silent save — autosave failures must not toast while the teacher types.
  const persist = (description: string) => {
    void onPersistRef.current(description).catch(() => undefined)
  }

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const description = event.currentTarget.value
    setValue(description)
    if (saveTimeout.current) clearTimeout(saveTimeout.current)
    saveTimeout.current = setTimeout(() => {
      saveTimeout.current = undefined
      persist(description)
    }, NOTES_AUTOSAVE_DEBOUNCE_MS)
  }

  // Flush the pending save when focus leaves the field, so closing the
  // drawer right after typing never drops the last keystrokes.
  const flushPendingSave = () => {
    if (!saveTimeout.current) return
    clearTimeout(saveTimeout.current)
    saveTimeout.current = undefined
    persist(valueRef.current)
  }

  // Unmount (drawer close / selection change) also flushes — blur does not
  // fire when the textarea is torn down while focused.
  useEffect(() => flushPendingSave, [])

  return (
    <Textarea
      label={t('arc_students_assessment_notes')}
      value={value}
      onChange={handleChange}
      onBlur={flushPendingSave}
      autosize
      minRows={NOTES_TEXTAREA_MIN_ROWS}
      disabled={disabled}
    />
  )
}
